// =============================================================================
//  Copyright   : 2019 semantics GmbH
//  Date        : $Date: 2026-05-27 14:18:05 +0200 (Wed, 27 May 2026) $
//  Author      : Dirk Rothe
//  Worker      : $Author: d.rothe $
//  Revision    : $Rev: 163319 $
//  Purpose     : process templateconfig from component/views.js
// =============================================================================
// ---Third-party imports
// ---Framework imports
import log from 'uix/logging';
import { get, isString, mapItems, dict, keys, values, flatten, isFunction, reversed, cloneDeep, includes, sorted } from 'ldash';
import { Views } from 'uix/registries';
import { checkAcl } from 'uix/auth/acl';
// ---Globals
// XXX: find a place for these
// only legacy wiki-bridge?
const PARTS = {
    ReleaseHistory: {
        type: 'ReleaseHistory',
        name: 'ReleaseHistory',
        filters: [
            {
                cacheKeys: [
                    'dname',
                    'cname',
                    'max',
                    'limit',
                    'withFacets'
                ],
                part: 'ReleaseHistory',
                type: 'getReleaseHistory',
                paramsFromPart: 'max, limit, withFacets',
                lazy: true
            }
        ]
    },
    Widget: {
        type: 'Widget',
        name: 'Widget',
        filters: [
            {
                part: 'Widget',
                type: 'getWidgetWiki',
                lazy: true,
                paramsFromPart: 'format, pool'
            }
        ]
    },
    WikiEmbed: {
        type: 'Wikiblock',
        name: 'WikiEmbed',
        filters: [
            {
                part: 'WikiEmbed',
                type: 'getWikiRendered',
                paramsFromPart: 'ident,condition,part'
            }
        ]
    },
    AllDocuments: {
        type: 'AllDocuments',
        name: 'AllDocuments',
        filters: [
            {
                part: 'AllDocuments',
                lazy: true,
                type: 'getAllDocuments'
            }
        ]
    }
};
let getClasses = (classes, filterfunc)=>{
    classes = mapItems(classes, (classname, classcfg)=>({
            classname,
            classcfg
        })).filter(filterfunc);
    return {
        items: classes,
        insertPart: (part)=>{
            // mutation
            classes.forEach(({ classcfg })=>classcfg.parts.push(part));
        }
    };
};
let getTabs = (classes, filterfunc)=>{
    let tabs = flatten(mapItems(classes, (classname, classcfg)=>mapItems(classcfg.tabs, (tabname, tabcfg)=>({
                classname,
                classcfg,
                tabname,
                tabcfg
            })))).filter(filterfunc);
    return {
        items: tabs,
        insertPart: (part)=>{
            // mutation
            tabs.forEach(({ tabcfg })=>tabcfg.parts.push(part));
        },
        update: (key, value)=>tabs.forEach((t)=>t[key] = value)
    };
};
let getParts = (classes, filterfunc)=>{
    let tabs = getTabs(classes, (e)=>true).items;
    let tabParts = flatten(tabs.map(({ tabcfg, ...rest })=>tabcfg.parts.filter((partcfg)=>filterfunc({
                ...partcfg,
                tabcfg,
                ...rest
            }))));
    //~ log.x(
    //~ mapItems(classes, (classname, classcfg) => classname, (classname, classcfg) => !classcfg.parts)
    //~ )
    let classParts = flatten(mapItems(classes, (classname, classcfg)=>classcfg.parts.filter((partcfg)=>filterfunc({
                ...partcfg,
                classname,
                classcfg
            }))));
    let parts = [
        ...tabParts,
        ...classParts
    ];
    return {
        items: parts,
        // mutation
        update: (key, value)=>parts.forEach((p)=>p[key] = value)
    };
};
let getPath = (classes, path)=>{
    /* process a xpr path
   */ let [cn, tn, pn_pt] = path.split('/');
    let result = {
        items: []
    };
    let target_type = null;
    // if tn starts with uppercase letter - assume it's a part
    if (tn && tn[0] == tn[0].toUpperCase()) {
        pn_pt = tn;
        tn = '*';
    }
    if (!pn_pt) {
        if (tn) {
            target_type = 'TAB';
            result = getTabs(classes, ({ classname, tabname })=>classname == cn && tabname == tn);
        } else {
            target_type = 'CLASS';
            result = getClasses(classes, ({ classname })=>classname == cn);
        }
    } else {
        target_type = 'PART';
        result = getParts(classes, ({ classname, tabname, name, type })=>classname == cn && (tn == '*' || tabname == tn) && (name == pn_pt || type == pn_pt));
    }
    if (result.items.length == 0) {
        log.warn('got no targets [%s] for path %o', target_type, path);
    }
    return result;
};
export const initializeTemplating = (iconfig, ddata, dcode)=>{
    let configureViewsFuncs = dcode.map(({ configureViews })=>configureViews).filter((e)=>e);
    let { dconfig, groups, client, name: dname } = ddata;
    if (!groups || !client) {
        log.error('basic user data needed in ddata');
    }
    // XXX: constructing a custom (non-compatible) user object
    // is problematic downstream
    let user = {
        groups,
        client,
        // groups is already domain-local-groups joined
        hasGroup: (name)=>includes(groups, name)
    } // for acls and cfg() eval
    ;
    let initializeStringPartForms = (partcfg)=>{
        if (isString(partcfg)) {
            return {
                type: partcfg
            };
        }
        return partcfg;
    };
    let configFilter = (name, class_tab_part)=>{
        let { cfg } = class_tab_part;
        if (!cfg) {
            return true;
        }
        if (isFunction(cfg)) {
            let dtype = dconfig.domain.type;
            return cfg({
                dname,
                dtype,
                iconfig,
                dconfig,
                user
            });
        } else {
            log.warn('cfg attribute needs to be a callable, but is %o', cfg);
            return false;
        }
    };
    let part_acl_cfg_filter = (partcfg)=>{
        // remove parts with acl failures
        let acl_ok = !partcfg.acl || checkAcl(partcfg.acl, user, dname);
        // remove parts with cfg failures
        return acl_ok && configFilter(partcfg.type, partcfg);
    };
    let transformParts = (parts)=>{
        // map raw string types to basic form
        parts = parts.map(initializeStringPartForms).filter(part_acl_cfg_filter);
        // move part-bound filters to tab level
        let filters = flatten(parts.map((partcfg)=>(partcfg.filters || []).map((filtercfg)=>{
                if (isString(filtercfg)) {
                    filtercfg = {
                        type: filtercfg
                    };
                }
                return {
                    ...filtercfg,
                    part: partcfg.name || partcfg.type
                };
            })));
        // remove filters from parts
        parts = parts.map((partcfg)=>{
            let { filters, ..._partcfg } = partcfg;
            return _partcfg;
        });
        // sanity checks
        parts.forEach((p)=>{
            if (!p.type) {
                log.error('%o has no type', p);
            }
        });
        return {
            parts,
            filters
        };
    };
    let transformTab = (name, tabcfg, i)=>{
        /* finalize configuration of view tab
     */ tabcfg = {
            ...tabcfg,
            name,
            parts: tabcfg.parts || []
        };
        let tab_acl_success = !tabcfg.acl || checkAcl(tabcfg.acl, user, dname);
        let order = tabcfg.order || i;
        if (!tab_acl_success) {
            // flag tab as authNeeded on acl failure
            tabcfg = {
                ...tabcfg,
                parts: [],
                filters: [],
                authNeeded: true,
                order
            };
        } else {
            let { parts, filters } = transformParts(tabcfg.parts);
            // merge partlevel and tablevel filters
            filters = [
                ...tabcfg.filters || [],
                ...filters
            ];
            tabcfg = {
                ...tabcfg,
                parts,
                filters,
                order
            };
        }
        return [
            name,
            tabcfg
        ];
    };
    let transformClass = (name, classcfg)=>{
        /* finalize configuration of view class
     */ let class_acl_success = !classcfg.acl || checkAcl(classcfg.acl, user, dname);
        classcfg = {
            ...classcfg,
            name
        };
        if (!classcfg.parts) {
            classcfg.parts = [];
        }
        classcfg.parts = classcfg.parts.map(initializeStringPartForms).filter(part_acl_cfg_filter);
        if (!class_acl_success) {
            // flag class as authNeeded on acl failure
            classcfg = {
                ...classcfg,
                tabs: {},
                authNeeded: true
            };
        } else {
            let tabs = dict(sorted(mapItems(classcfg.tabs, transformTab, configFilter), ([name, tab])=>tab.order));
            classcfg = {
                ...classcfg,
                tabs,
                filters: classcfg.filters || []
            };
        }
        return [
            name,
            classcfg
        ];
    };
    let classes = dict(mapItems(Views, transformClass, configFilter));
    // apply domain chain from root to leaf
    if (configureViewsFuncs.length > 0) {
        // configureViewsFuncs can be mutating (xpr is mutating)
        classes = cloneDeep(classes);
        for (let f of reversed(configureViewsFuncs)){
            let res = f({
                classes,
                xpr: (path)=>getPath(classes, path)
            });
            if (res) {
                classes = res;
            }
        }
        delete classes.get;
    }
    //~ log.x(classes.analytics.tabs.job)
    //~ log.x(JSON.stringify(classes.analytics.tabs))
    return {
        classes,
        parts: PARTS
    };
};
